import React from "react";
import { testimonialGroups } from "../data/testimonials";
import { TestimonialCarousel } from "../components/feedback/TestimonialCarousel";
import { ParticleBackground } from "../components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { ShareYourStoryForm } from "@/components/feedback/ShareYourStoryForm";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { GraduationCap } from "lucide-react";

export default function Feedback() {
  return (
    <>
      <SEOHead
        title="Customer Feedback & Testimonials | Real Stories"
        description="Read real testimonials from NHS staff, schools, therapists, and families using Samboards. Hear how our sensory tools help with anxiety, ADHD, autism, and focus."
        canonical="/feedback"
        keywords="samboards reviews, sensory tools testimonials, NHS feedback, autism sensory tools reviews, ADHD support feedback"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Feedback", url: "/feedback" }
      ]} />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />

        <Page>
        <section className="space-y-10 pb-24">
        <header className="mb-10 md:mb-14 max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.2em] text-emerald-300 uppercase mb-3">Social Proof</p>
          <h1 className="text-3xl md:text-[40px] leading-tight font-semibold page-title-gradient">
            Stories from people who use Samboards every day.
          </h1>
          <p className="mt-4 text-sm md:text-base text-slate-300/85">
            From NHS wards to home offices, classrooms and rehab centres, Samboards travel with people into the spaces
            where stress, anxiety and distraction usually take over. These are their words, not ours.
          </p>
        </header>

        <div className="space-y-8 md:space-y-10">
          {testimonialGroups.map((group) => (
            <TestimonialCarousel key={group.id} group={group} />
          ))}
        </div>

        {/* Visual separator before MBA study section */}
        <Separator className="my-12 md:my-16 bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent h-[2px]" />

        {/* MBA NHS Frontline Study Section */}
        <div id="mba-nhs-study" className="mt-16 space-y-6">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h2 className="text-2xl md:text-3xl font-semibold text-emerald-300 tracking-wide">
                MBA NHS Frontline Wellbeing Study (University of Bristol, 2021)
              </h2>
              <Badge className="bg-emerald-500/15 border-emerald-400/30 text-emerald-300 text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1">
                <GraduationCap className="h-3 w-3" />
                Research
              </Badge>
            </div>
            <p className="text-sm md:text-base text-slate-300/85 leading-relaxed">
              Extracts from an MBA project conducted during the peak of COVID in January 2021, exploring how Samboards supported NHS staff wellbeing.
            </p>
            
            {/* Study Context Strip */}
            <div className="mt-4 pt-4 border-t border-emerald-400/10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400/90">
              <div>
                <span className="font-medium text-emerald-300/70">Study type:</span>{" "}
                <span>MBA research</span>
              </div>
              <div>
                <span className="font-medium text-emerald-300/70">Location:</span>{" "}
                <span>University of Bristol, NHS frontline staff (2021)</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {[
              {
                id: "mba-1",
                quote: "Working in a hospital under the current circumstances is particularly challenging and stressful. With the job, ensuring patients flow and admission are bedded safely around the trust, a lot of stress is accumulated.\n\nMy colleague Bev presented us with a stress relieving device which helps taking the mind off a million thoughts and sewing a line of thinking back together. I personally have mild ADHD and it helped me focus on goals and targets one step at a time without drifting into a million outstanding tasks.\n\nA very positive tool and comes in a portable form which is very helpful as well as being safe as easily wipeable.",
                variant: "primary"
              },
              {
                id: "mba-2",
                quote: "Not flexible enough to withstand my stress relief requirement, I would have liked it to be a bit more malleable. Loved the unicorn design. A brighter colour may have made me pick it up and use it a bit more.",
                variant: "secondary"
              },
              {
                id: "mba-3",
                quote: "Used the prickly tile during a presentation that I had to give. I found it a distraction from the nerves of presenting. It took my mind away from the fear of stuttering. It worked and I felt incredibly relaxed whilst carrying out the presentation.",
                variant: "primary"
              },
              {
                id: "mba-4",
                quote: "Love the different textures on the tiles, would like to see in brighter colours such as pink, yellow, green, blue.",
                variant: "secondary"
              },
              {
                id: "mba-5",
                quote: "Given one to my youngest child during the home schooling to try and focus him and keep him still. I found the days where he had the tile to 'fiddle' with his concentration improved somewhat and he was less inclined to get up out of his chair.",
                variant: "primary"
              },
              {
                id: "mba-6",
                quote: "Used in work to reduce my stress levels when getting into situations that would normally created some level of anxiety. I found having it on my lanyard meant it was easy to access and provided a level of distraction.",
                variant: "secondary"
              },
              {
                id: "mba-7",
                quote: "Great product, easily cleaned, would like it in different colours and patterns.\n\nMy daughter would love the unicorn tile if it was a different colour. Did not use it as stress reduction but my daughter loved it.",
                variant: "primary"
              }
            ].map((testimonial) => (
              <div
                key={testimonial.id}
                className={testimonial.variant === "primary" ? "mba-card-primary" : "mba-card-secondary"}
                tabIndex={0}
              >
                <span className="mba-label">
                  NHS staff – MBA study
                </span>
                <p className="mba-text">
                  "{testimonial.quote}"
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Share Your Story Section */}
        <div className="mt-20 pt-12 border-t border-slate-700/40">
          <div className="text-center mb-8 max-w-2xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.2em] text-emerald-300 uppercase mb-3">
              Join the conversation
            </p>
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-50 mb-3">
              Have Samboards helped you?
            </h2>
            <p className="text-sm md:text-base text-slate-300/85">
              We'd love to hear your story. Share how Samboards have made a difference for you, your family, students, or patients.
            </p>
          </div>

          <ShareYourStoryForm />
        </div>
      </section>
        </Page>
      </div>
    </>
  );
}
